import { useEffect, useState } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import { ArrowRight, Check, Landmark, Heart, Copy } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { HOME } from "@/constants/testIds";

const WA_BASE =
  "https://wa.me/41789222327?text=Hola%2C%20quiero%20sumarme%20al%20C%C3%ADrculo%20Ni%C3%B1agordita.%20%C2%BFMe%20cuentas%20c%C3%B3mo%20ser%20parte%3F";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.13 6.744 3.05 9.38L1.05 31.36l6.18-1.974A15.9 15.9 0 0 0 16.004 32C24.83 32 32 24.822 32 16S24.83 0 16.004 0Zm9.32 22.6c-.388 1.094-1.926 2-3.152 2.266-.838.178-1.932.32-5.616-1.206-4.71-1.952-7.744-6.74-7.98-7.05-.226-.31-1.902-2.532-1.902-4.83 0-2.298 1.176-3.426 1.592-3.894.388-.437.85-.546 1.13-.546.276 0 .553.003.794.014.255.012.6-.097.94.717.388.926 1.32 3.224 1.434 3.456.118.232.196.504.04.814-.146.31-.272.435-.504.71-.232.276-.49.616-.7.826-.232.232-.474.484-.204.95.27.464 1.202 1.982 2.58 3.21 1.772 1.58 3.27 2.068 3.736 2.302.466.232.737.196 1.01-.118.272-.31 1.166-1.36 1.476-1.826.31-.466.62-.388 1.05-.232.43.155 2.74 1.292 3.21 1.524.466.232.776.348.89.542.115.193.115 1.106-.273 2.2Z" />
  </svg>
);

const Reveal = ({ children, className, y = 30, delay = 0 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, filter: "blur(8px)", y }}
    whileInView={{ opacity: 1, filter: "blur(0px)", y: 0 }}
    viewport={{ once: true, margin: "-8%" }}
    transition={{ duration: 0.7, ease: "easeOut", delay }}
  >
    {children}
  </motion.div>
);

const Header = () => (
  <header
    data-testid={HOME.siteHeader}
    className="fixed top-0 inset-x-0 z-50 bg-[#FDFBF7]/85 backdrop-blur-md border-b border-[#EBE3E3]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 h-16 md:h-20 flex items-center justify-between">
      <a href="#top" className="flex items-center gap-2 group" data-testid={HOME.headerLogo}>
        <Heart className="w-4 h-4 text-[#8C2131] fill-[#8C2131] transition-transform group-hover:scale-110" />
        <span className="header-brand text-lg md:text-xl tracking-tight">Círculo Niñagordita</span>
      </a>
      <a
        data-testid={HOME.headerContactBtn}
        href={WA_BASE}
        target="_blank"
        rel="noopener noreferrer"
        className="hidden sm:inline-flex items-center gap-2 bg-[#8C2131] text-[#FDFBF7] px-5 py-2.5 text-xs uppercase tracking-widest font-medium hover:bg-[#701A27] transition-colors duration-300 rounded-sm"
      >
        <WhatsAppIcon className="w-4 h-4" />
        Contacto
      </a>
    </div>
  </header>
);

const FloatingContact = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <motion.a
      data-testid={HOME.floatingContactBtn}
      href={WA_BASE}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Contacto por WhatsApp"
      className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 bg-[#8C2131] text-[#FDFBF7] pl-4 pr-5 py-3.5 rounded-full shadow-[0_10px_30px_rgba(140,33,49,0.4)]"
      initial={{ opacity: 0, transform: "translateY(20px)" }}
      animate={visible ? { opacity: 1, transform: "translateY(0px)" } : { opacity: 0, transform: "translateY(20px)" }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={{ pointerEvents: visible ? "auto" : "none" }}
    >
      <WhatsAppIcon className="w-5 h-5" />
      <span className="text-xs uppercase tracking-widest font-medium hidden sm:inline">Contacto</span>
    </motion.a>
  );
};

const Hero = () => (
  <section id="top" data-testid={HOME.heroSection} className="relative pt-28 md:pt-32 pb-16 md:pb-24">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-12 gap-y-14 lg:gap-x-12 items-center">
      <div className="lg:col-span-7">
        <Reveal>
          <span className="block text-xs md:text-sm uppercase tracking-[0.25em] font-medium text-[#8C2131] mb-5">
            Una campaña privada · 250 mujeres
          </span>
        </Reveal>
        <Reveal y={32} delay={0.05}>
          <h1 className="mt-1">
            <span className="block text-sm md:text-base font-medium uppercase tracking-[0.45em] text-[#1C1A18]/55 mb-3">
              CÍRCULO
            </span>
            <span className="film-title block text-[#8C2131] text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.82]">
              NIÑAGORDITA
            </span>
          </h1>
        </Reveal>
        <Reveal y={32} delay={0.1}>
          <p className="mt-8 max-w-md text-lg md:text-xl font-medium leading-relaxed text-[#5A554D]">
            Sé parte de las 250 mujeres que llevarán esta historia al cine.
          </p>
        </Reveal>
        <Reveal y={32} delay={0.15}>
          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <a
              data-testid={HOME.heroJoinBtn}
              href="#tarjetas"
              className="inline-flex items-center justify-center gap-2 bg-[#8C2131] text-[#FDFBF7] px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-[#701A27] transition-colors duration-300 rounded-sm"
            >
              Quiero sumarme al Círculo
            </a>
            <a
              data-testid={HOME.heroAboutBtn}
              href="#de-que-se-trata"
              className="inline-flex items-center justify-center gap-2 border border-[#1C1A18] text-[#1C1A18] px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-[#1C1A18] hover:text-[#FDFBF7] transition-colors duration-300 rounded-sm"
            >
              De qué se trata
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-5">
        <Reveal y={32} delay={0.1}>
          <div className="relative flex justify-center lg:justify-end">
            <img
              data-testid={HOME.heroImage}
              alt="Ilustración del corazón de Niñagordita"
              className="w-full max-w-xs sm:max-w-sm lg:max-w-md mix-blend-multiply will-change-transform select-none pointer-events-none"
              src="https://customer-assets.emergentagent.com/job_ninagordita-pelicula/artifacts/znd3uzr2_cora_3_blanco.png"
            />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const NarrativeBlock = ({ title, children, id, boxed }) => (
  <Reveal>
    <article
      id={id}
      className={boxed ? "bg-[#F4F0E6] p-8 md:p-14 rounded-sm scroll-mt-28" : "scroll-mt-28"}
    >
      <h2 className={`font-serif ${boxed ? "text-3xl md:text-4xl" : "text-3xl md:text-4xl lg:text-5xl"} leading-tight tracking-tight`}>
        {title}
      </h2>
      <div className="mt-7 space-y-5 text-lg font-normal leading-relaxed text-[#5A554D]">{children}</div>
    </article>
  </Reveal>
);

const Narrative = () => (
  <section data-testid={HOME.narrativeSection} className="py-20 md:py-28">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
      <div className="lg:grid lg:grid-cols-12">
        <div className="lg:col-start-3 lg:col-span-8">
          <Reveal>
            <p
              data-testid={HOME.basedOnNote}
              className="text-xs md:text-sm uppercase tracking-[0.22em] text-[#8C2131]/80 mb-8 md:mb-10 leading-relaxed"
            >
              Un proyecto original basado en la novela Niñagordita
              <br />
              de Belinda Palacios
            </p>
          </Reveal>
          <div className="space-y-20 md:space-y-28">
            <NarrativeBlock id="de-que-se-trata" title="Una pregunta que me tomó años responder">
              <p>
                Niñagordita nació de una pregunta que me tomó años poder responder:{" "}
                <em className="text-[#1C1A18] not-italic font-normal">¿cómo se destruye a una chica?</em>
              </p>
              <p>
                Cómo una chica joven, sana y con todo el futuro por delante termina convencida de que no vale nada
                —de que su cuerpo no vale nada, de que sus sueños no valen nada, de que todo es inapropiado en ella y
                su voz no es importante—.
              </p>
              <p>
                Y hasta qué punto esto deja huellas en nosotras y puede comprometer a largo plazo nuestra autoestima y
                nuestra salud mental.
              </p>
            </NarrativeBlock>
            <NarrativeBlock title="Una herida que no era solo mía">
              <p>
                La respuesta la encontré escribiendo una novela. La publiqué en 2022, y entonces pasó lo que nunca me
                hubiera imaginado: decenas de lectoras de edades muy variadas me escribieron para decir “esta también
                es mi historia”.
              </p>
              <p>
                Ahí entendí que el fantasma de la niñagordita —esa voz que desde la adolescencia se había empecinado en
                rebajarme— no era una herida mía: era algo que todas, en algún punto, habíamos vivido.
              </p>
            </NarrativeBlock>
            <NarrativeBlock title="Del libro a la película">
              <p>
                Esta herida compartida me ha llevado a conocer a mujeres maravillosas que me convencieron de trasladar
                la historia al cine.
              </p>
              <p>
                Por nosotras, las que ya lo vivimos, pero también para abrir conversaciones urgentes que nos permitan
                proteger a las nuevas generaciones antes de que sea demasiado tarde y el daño ya esté hecho.
              </p>
              <p>Por eso, queremos que esto sea más que una película: queremos que sea un movimiento.</p>
              <p>
                Un movimiento que ponga en claro, de una vez por todas, que el valor de una chica no se mide en relación
                con el canon de belleza de turno, y que ninguna debería aprender que es necesario hacerse pequeña para
                merecer ser amada.
              </p>
            </NarrativeBlock>
            <NarrativeBlock title="250 mujeres para llevar esta historia al cine" boxed>
              <p>Hoy estamos a un paso de lograrlo.</p>
              <p>
                Y hemos decidido que la mejor manera de completar lo que nos falta es apoyándonos entre nosotras:
                invitando a otras mujeres a ser parte de esta aventura, la de llevar al cine una historia que nos
                recuerda que somos muchísimo más que un cuerpo, y que nuestras ideas también merecen un lugar.
              </p>
            </NarrativeBlock>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const TierCard = ({ testId, badge, label, price, desc, features, note, ctaText, amount, featured, btnTestId, onSelect }) => (
  <Reveal>
    <div
      data-testid={testId}
      className={`relative flex flex-col h-full p-8 md:p-10 rounded-sm transition-all duration-500 group ${
        featured
          ? "bg-[#FDFBF7] text-[#1C1A18] shadow-[0_20px_60px_rgba(140,33,49,0.25)] md:-translate-y-3"
          : "bg-[#262220] text-[#FDFBF7] border border-white/10 hover:border-[#E8A4AE]/40 hover:-translate-y-1"
      }`}
    >
      {badge && (
        <span className="absolute top-0 right-8 -translate-y-1/2 bg-[#8C2131] text-[#FDFBF7] text-[10px] uppercase tracking-[0.2em] px-4 py-1.5 rounded-sm">
          {badge}
        </span>
      )}
      <span className={`text-sm uppercase tracking-wider font-medium mb-4 ${featured ? "text-[#8C2131]" : "text-[#E8A4AE]"}`}>
        {label}
      </span>
      <div className="font-serif text-4xl md:text-5xl mb-3">{price}</div>
      <p className={`text-sm md:text-[15px] font-normal leading-relaxed mb-7 ${featured ? "text-[#5A554D]" : "text-[#cfc7bd]"}`}>
        {desc}
      </p>
      <ul className="space-y-4 mb-8 flex-grow text-sm md:text-[15px] font-normal">
        {features.map((f, i) => (
          <li key={i} className="flex gap-3">
            <Check className={`w-5 h-5 shrink-0 mt-0.5 ${featured ? "text-[#8C2131]" : "text-[#E8A4AE]"}`} />
            <span className={featured ? "text-[#5A554D]" : "text-[#cfc7bd]"}>{f}</span>
          </li>
        ))}
      </ul>
      {note && <p className="text-xs leading-relaxed italic mb-8 text-[#9c958b]">{note}</p>}
      <button
        data-testid={btnTestId}
        type="button"
        onClick={() => onSelect(amount)}
        className={`inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm uppercase tracking-widest font-medium rounded-sm transition-colors duration-300 ${
          featured
            ? "bg-[#8C2131] text-[#FDFBF7] hover:bg-[#701A27]"
            : "border border-[#FDFBF7]/60 text-[#FDFBF7] hover:bg-[#FDFBF7] hover:text-[#1C1A18]"
        }`}
      >
        {ctaText}
      </button>
    </div>
  </Reveal>
);

const bankRows = [
  ["Banco", "BCP"],
  ["Titular", "NG FILMS S.A.C."],
  ["Número de cuenta", "1947321369130"],
  ["N° de cuenta interbancaria (CCI)", "00219400732136913093"],
  ["Moneda", "USD (dólares)"],
];

const Tiers = () => {
  const [open, setOpen] = useState(false);
  const [monto, setMonto] = useState(null);
  const openWithAmount = (amount) => {
    setMonto(amount);
    setOpen(true);
  };
  const rows = monto ? [["Monto", monto], ...bankRows] : bankRows;
  return (
    <section
      id="tarjetas"
      data-testid={HOME.tiersSection}
      className="py-20 md:py-28 bg-[#1C1A18] text-[#FDFBF7] scroll-mt-20"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <span className="block text-xs md:text-sm uppercase tracking-[0.25em] font-medium text-[#E8A4AE] mb-5">
              Entra al Círculo
            </span>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight">Elige cómo sumarte</h2>
            <p className="mt-5 text-lg font-normal text-[#cfc7bd]">
              Tres formas de ser parte de la historia. Tú eliges hasta dónde quieres acompañarnos.
            </p>
          </div>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-3 gap-6 lg:gap-8">
          <TierCard
            testId={HOME.tierCard250}
            btnTestId={HOME.tierWhatsApp250}
            featured
            badge="Más elegida"
            label="Aliada del Círculo Niñagordita"
            price="USD 250"
            desc="Sé parte de las 250 mujeres que llevarán esta historia al cine."
            features={[
              "Tu nombre en los créditos finales de la película como Aliada de Niñagordita.",
              "Tu nombre en los agradecimientos de la tercera edición de Niñagordita, edición película.",
              "Invitación a una proyección exclusiva de la película, solo para el Círculo.",
            ]}
            ctaText="Quiero ser Aliada"
            amount="USD 250"
            onSelect={openWithAmount}
          />
          <TierCard
            testId={HOME.tierCard1200}
            btnTestId={HOME.tierWhatsApp1200}
            label="Madrina de un área de la película"
            price="USD 1,200"
            desc="Deja una huella más profunda amadrinando una de las áreas creativas de la película."
            features={[
              "Tu nombre asociado a los créditos del área que elijas: arte, vestuario, maquillaje y peinado, fotografía o música.",
              "Tu nombre en los agradecimientos de la tercera edición de Niñagordita, edición película.",
              "Invitación a una proyección exclusiva de la película, solo para el Círculo.",
            ]}
            ctaText="Quiero amadrinar un área"
            amount="USD 1,200"
            onSelect={openWithAmount}
          />
          <TierCard
            testId={HOME.tierCard5000}
            btnTestId={HOME.tierWhatsApp5000}
            label="Productora Asociada"
            price="USD 5,000"
            desc="Un reconocimiento especial para quienes hacen posible que esta película llegue a la pantalla."
            features={[
              "Tu nombre en los créditos iniciales de la película como Productora Asociada.",
              "Tu nombre en los agradecimientos de la tercera edición de Niñagordita, edición película.",
              "Reconocimiento destacado dentro del proyecto.",
              "Invitación a una proyección exclusiva de la película, solo para el Círculo.",
            ]}
            note="El título de Productora Asociada es honorífico y no otorga derechos de propiedad intelectual ni derechos de explotación sobre la película o el proyecto."
            ctaText="Quiero ser Productora Asociada"
            amount="USD 5,000"
            onSelect={openWithAmount}
          />
        </div>
        <Reveal>
          <div className="mt-16 text-center">
            <p className="text-xl md:text-2xl font-medium text-[#FDFBF7] mb-2">
              ¿Ya sabes con qué monto quieres apoyar?
            </p>
            <p className="text-base md:text-lg text-[#cfc7bd] mb-7 max-w-xl mx-auto">
              Puedes hacer tu aporte directamente por transferencia bancaria.
            </p>
            <button
              data-testid={HOME.bankInfoBtn}
              type="button"
              onClick={() => openWithAmount(null)}
              className="inline-flex items-center gap-2 bg-[#E8A4AE] text-[#1C1A18] px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-[#FDFBF7] transition-colors duration-300 rounded-sm"
            >
              <Landmark className="w-4 h-4" />
              Ver datos para depósito
            </button>
          </div>
        </Reveal>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            data-testid={HOME.bankInfoDialog}
            className="bg-[#FDFBF7] text-[#1C1A18] border-[#EBE3E3]"
          >
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Datos para depósito directo</DialogTitle>
            </DialogHeader>
            <div className="mt-2 divide-y divide-[#EBE3E3]">
              {rows.map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-widest font-medium text-[#8C2131]">{k}</p>
                    <p className="text-sm md:text-base font-medium truncate">{v}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(v)}
                    className="shrink-0 text-[#5A554D] hover:text-[#8C2131] transition-colors"
                    aria-label={`Copiar ${k}`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <a
              href="https://wa.me/41789222327?text=Hola%2C%20ya%20hice%20un%20dep%C3%B3sito%20para%20sumarme%20al%20C%C3%ADrculo%20Ni%C3%B1agordita.%20Te%20comparto%20mi%20comprobante."
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center justify-center gap-2 w-full bg-[#8C2131] text-[#FDFBF7] px-6 py-3.5 text-sm uppercase tracking-widest font-medium rounded-sm hover:bg-[#701A27] transition-colors duration-300"
            >
              <WhatsAppIcon className="w-4 h-4" />
              Enviar mi comprobante por WhatsApp
            </a>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

const fichaTecnica = [
  ["Produce", "NG Films"],
  ["Directora y coguionista", "Camila Vidal"],
  ["Autora de la novela y coguionista", "Belinda Palacios"],
  ["Protagoniza", "Silvana Cañote"],
  ["Producción ejecutiva", "Belinda Palacios, Silvana Cañote y Diego Vives"],
  ["Dirección de fotografía", "Isabel Mazzolini"],
  ["Dirección de arte", "María Cristina Martínez"],
  ["Rodaje previsto", "Noviembre de 2026, en Lima"],
  ["Estreno previsto", "Agosto de 2027"],
];

const Film = () => (
  <section data-testid={HOME.filmSection} className="py-20 md:py-28">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-12 gap-12 lg:gap-16">
      <div className="lg:col-span-7">
        <Reveal>
          <span className="block text-xs md:text-sm uppercase tracking-[0.25em] font-medium text-[#8C2131] mb-5">
            Sobre la película
          </span>
          <h2 className="film-title text-5xl md:text-6xl lg:text-7xl text-[#1C1A18] leading-[0.9]">NIÑAGORDITA</h2>
          <p className="mt-5 font-serif italic text-xl md:text-2xl text-[#8C2131] leading-snug">
            Una película sobre el cuerpo, el deseo y esas historias de amor que nos obligan a borrarnos.
          </p>
          <div className="mt-7 space-y-5 text-lg font-normal leading-relaxed text-[#5A554D]">
            <p>Niñagordita es una película de ficción que transcurre en Lima, entre 2002 y 2009.</p>
            <p>
              Tali es una joven adulta que sueña con ser diseñadora de modas, pero vive atrapada entre hombres que la
              desean mientras le enseñan a desaparecer y una sociedad que le exige hacerse pequeña para merecer amor.
              Entre dietas silenciosas y hábitos autodestructivos, dibuja figurines que imaginan otros cuerpos posibles.
              Cuando una nueva relación fractura su identidad, Tali deberá destruir la versión que creó de sí misma para
              ser querida antes de poder ocupar, por fin, su lugar en el mundo.
            </p>
            <p>
              La película aborda temas como autoestima, salud mental, presión estética, deseo, vínculos afectivos y la
              forma en la que muchas mujeres aprenden de forma sistemática a desconfiar de su propio valor.
            </p>
          </div>
          <a
            data-testid={HOME.filmWebsiteBtn}
            href="https://www.ninagordita.pe/"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-9 inline-flex items-center gap-2 bg-[#1C1A18] text-[#FDFBF7] px-7 py-3.5 text-xs md:text-sm uppercase tracking-widest font-medium hover:bg-[#8C2131] transition-colors duration-300 rounded-sm"
          >
            Conoce más sobre la película y el proyecto Niñagordita
            <ArrowRight className="w-4 h-4" />
          </a>
        </Reveal>
      </div>
      <div className="lg:col-span-5">
        <Reveal y={32}>
          <div className="bg-[#F4F0E6] p-8 md:p-10 rounded-sm">
            <h3 className="font-serif text-2xl mb-6">Ficha técnica</h3>
            <ul className="space-y-5">
              {fichaTecnica.map(([k, v]) => (
                <li key={k}>
                  <p className="text-xs uppercase tracking-widest font-medium text-[#8C2131]">{k}</p>
                  <p className="text-base md:text-lg font-medium text-[#1C1A18]">{v}</p>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Closing = () => (
  <section data-testid={HOME.closingSection} className="py-24 md:py-36 bg-[#F4F0E6]">
    <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
      <Reveal>
        <div className="flex items-center justify-center gap-3 py-2">
          <span className="h-px w-12 bg-[#d8cfc0]" />
          <Heart className="w-4 h-4 text-[#8C2131] fill-[#8C2131]" />
          <span className="h-px w-12 bg-[#d8cfc0]" />
        </div>
      </Reveal>
      <Reveal y={32}>
        <p className="mt-8 font-serif text-3xl md:text-5xl lg:text-[3.5rem] leading-[1.15] tracking-tight">
          Porque las mujeres que lideran el futuro también impulsan las ideas que lo transforman.
        </p>
      </Reveal>
      <Reveal y={32}>
        <a
          data-testid={HOME.closingWhatsAppBtn}
          href={WA_BASE}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-12 inline-flex items-center justify-center gap-2 bg-[#8C2131] text-[#FDFBF7] px-10 py-5 text-sm uppercase tracking-widest font-medium hover:bg-[#701A27] transition-colors duration-300 rounded-sm"
        >
          <WhatsAppIcon className="w-5 h-5" />
          Te espero en el Círculo
        </a>
      </Reveal>
    </div>
  </section>
);

const Home = () => (
  <div className="grain relative min-h-screen bg-[#FDFBF7] text-[#1C1A18] overflow-x-hidden">
    <Header />
    <FloatingContact />
    <Hero />
    <Narrative />
    <Tiers />
    <Film />
    <Closing />
  </div>
);

function App() {
  return (
    <div className="App">
      <Home />
    </div>
  );
}

export default App;
